/**
 * File: chatService.ts
 * Description: Service for backend chat communication with context support
 * Author: System
 * Created: 2025-08-27
 * Last Modified: 2025-08-27
 */

import { ChatRequest, ChatResponse, IChatService } from '../types/chat';
import { KeyValue } from '../types/context';
import { logger } from '../utils/logger';

import { ApiClient } from './apiClient';

/**
 * Chat service for backend communication
 */
class ChatService implements IChatService {
  private apiClient: ApiClient;

  constructor() {
    this.apiClient = new ApiClient();
  }

  /**
   * Send chat message to backend with context
   */
  public async sendMessage(
    projectId: number,
    userInput: string,
    context: KeyValue[],
  ): Promise<ChatResponse> {
    if (!projectId || projectId <= 0) {
      throw new Error('Valid project ID is required for chat');
    }

    if (!userInput.trim()) {
      throw new Error('User input cannot be empty');
    }

    const requestPayload: ChatRequest = {
      user_input: userInput.trim(),
      context: this.sanitizeContext(context),
    };

    try {
      const response = await this.apiClient.post<ChatResponse>(
        `/api/project/${projectId}/chat`,
        requestPayload,
      );

      // Validate response structure
      if (!response.answers) {
        throw new Error('Invalid response format: missing answers field');
      }

      // Process actions if present
      if (response.actions) {
        await this.processActions(response.actions);
      }

      return {
        answers: response.answers,
        references: response.references || [],
        actions: response.actions || [],
        execution_steps: response.execution_steps || [],
        session_id: response.session_id,
        chat_history: response.chat_history || [],
      };
    } catch (error) {
      logger.error('Chat service error:', error);
      
      // Re-throw with more context
      if (error instanceof Error) {
        throw new Error(`Chat service failed: ${error.message}`);
      }
      
      throw new Error('Chat service failed with unknown error');
    }
  }

  /**
   * Sanitize context data before sending to backend
   */
  private sanitizeContext(context: KeyValue[]): KeyValue[] {
    return context
      .filter(item => 
        item.key && 
        item.value && 
        typeof item.key === 'string' && 
        typeof item.value === 'string',
      )
      .map(item => ({
        key: item.key.trim(),
        value: this.sanitizeValue(item.value),
      }))
      .filter(item => item.key.length > 0 && item.value.length > 0)
      .slice(0, 50); // Limit context size to prevent API payload issues
  }

  /**
   * Sanitize individual context values
   */
  private sanitizeValue(value: string): string {
    return value
      .trim()
      .substring(0, 1000) // Limit individual value size
      .replace(/[\r\n\t]+/g, ' ') // Replace newlines/tabs with spaces
      .replace(/\s+/g, ' '); // Replace multiple spaces with single space
  }

  /**
   * Get chat history for a project (if backend supports it)
   * Note: This method is prepared for future backend support
   */
  public async getChatHistory(_projectId: number): Promise<ChatResponse[]> {
    // Placeholder for future implementation
    // The current backend API doesn't provide chat history endpoint
    throw new Error('Chat history not yet supported by backend');
  }

  /**
   * Clear chat history for a project (if backend supports it)
   * Note: This method is prepared for future backend support
   */
  public async clearChatHistory(_projectId: number): Promise<void> {
    // Placeholder for future implementation
    // The current backend API doesn't provide chat history management
    throw new Error('Chat history management not yet supported by backend');
  }

  /**
   * Get or create chat session with history for a project
   */
  public async getOrCreateSession(projectId: number): Promise<{
    session_id: string;
    messages: Array<{
      id: string;
      role: 'user' | 'assistant';
      content: string;
      timestamp?: string;
      type: string;
    }>;
    message_count: number;
    has_history: boolean;
    status: string;
    message: string;
  }> {
    if (!projectId || projectId <= 0) {
      throw new Error('Valid project ID is required');
    }

    try {
      const response = await this.apiClient.get(
        `/api/project/${projectId}/chat/session`,
      ) as {
        session_id: string;
        messages: {
          id: string;
          role: 'user' | 'assistant';
          content: string;
          timestamp?: string;
          type: string;
        }[];
        message_count: number;
        has_history: boolean;
        status: string;
        message: string;
      };

      return response;
    } catch (error) {
      logger.error('Session service error:', error);
      
      if (error instanceof Error) {
        throw new Error(`Session service failed: ${error.message}`);
      }
      
      throw new Error('Session service failed with unknown error');
    }
  }

  /**
   * Refresh chat session - clears history and returns fresh session info
   */
  public async clearUserChatHistory(projectId: number): Promise<{
    session_id: string;
    project_id: number;
    user_id: string;
    chat_history: any[];
    message_count: number;
    message: string;
  }> {
    if (!projectId || projectId <= 0) {
      throw new Error('Valid project ID is required');
    }

    try {
      const response = await this.apiClient.post(
        `/api/project/${projectId}/chat/session/refresh`,
      ) as {
        session_id: string;
        project_id: number;
        user_id: string;
        chat_history: any[];
        message_count: number;
        message: string;
      };

      return response;
    } catch (error) {
      logger.error('Clear history service error:', error);
      
      if (error instanceof Error) {
        throw new Error(`Clear history failed: ${error.message}`);
      }
      
      throw new Error('Clear history failed with unknown error');
    }
  }

  /**
   * Process chat actions from backend response
   */
  private async processActions(actions: any[]): Promise<void> {
    for (const action of actions) {
      await this.processAction(action);
    }
  }

  /**
   * Process individual chat action
   */
  private async processAction(action: any): Promise<void> {
    switch (action.type) {
      case 'confirmation':
        await this.handleConfirmation(action);
        break;
      case 'refreshView':
        this.handleRefreshView(action);
        break;
      case 'uiEdit':
        this.handleUIEdit(action);
        break;
      case 'navigate':
        this.handleNavigation(action);
        break;
      case 'showDiff':
        this.handleShowDiff(action);
        break;
      case 'delegate_agent':
        this.handleAgentDelegation(action);
        break;
      case 'adr_suggest':
        this.handleADRSuggestions(action);
        break;
      case 'adr_create':
        this.handleADRCreate(action);
        break;
      case 'adr_generate':
        this.handleADRGenerate(action);
        break;
      case 'adr_summary':
        this.handleADRSummary(action);
        break;
      default:
        logger.warn('Unknown action type:', action.type);
    }
  }

  /**
   * Handle confirmation actions
   */
  private async handleConfirmation(action: any): Promise<void> {
    if (action.confirmation_required) {
      const isADRConfirmation = action.data?.target_agent === 'adr_create_confirmed';
      
      let confirmed = false;
      
      if (isADRConfirmation) {
        // Show structured ADR preview for confirmation
        confirmed = this.showADRDefinitionConfirmation(action);
      } else {
        // Standard confirmation dialog
        confirmed = globalThis.confirm(`${action.title}\n\n${action.description}`);
      }
      
      if (confirmed && action.data) {
        try {
          // Get current project ID from URL or context
          const projectId = this.getCurrentProjectId();
          const result = await this.confirmAction(projectId, action);
          
          // Process any resulting actions
          if (result.actions) {
            await this.processActions(result.actions);
          }
        } catch (error) {
          logger.error('Failed to confirm action:', error);
        }
      }
    }
  }

  /**
   * Show structured ADR definition confirmation dialog
   */
  private showADRDefinitionConfirmation(action: any): boolean {
    try {
      const adrData = typeof action.data.content === 'string' 
        ? JSON.parse(action.data.content) 
        : action.data.content;
      
      const confirmMessage = `${action.title}

📋 Title: ${adrData.title}

📝 Context: ${adrData.contextDescription}

🤖 Generation Instructions: ${adrData.llmInstructions}

📊 Source: ${adrData.source}

Would you like to add this ADR definition to your project?`;
      
      return globalThis.confirm(confirmMessage);
    } catch (error) {
      logger.error('Error parsing ADR data for confirmation:', error);
      return globalThis.confirm(`${action.title}\n\n${action.description}`);
    }
  }

  /**
   * Confirm action with backend
   */
  public async confirmAction(projectId: number, actionData: any): Promise<any> {
    try {
      const response = await this.apiClient.post(
        `/api/project/${projectId}/chat/action/confirm`,
        actionData,
      ) as any;
      
      // Process any resulting actions
      if (response.actions) {
        await this.processActions(response.actions);
      }
      
      return response;
    } catch (error) {
      logger.error('Action confirmation error:', error);
      throw error;
    }
  }

  /**
   * Handle UI refresh actions
   */
  private handleRefreshView(action: any): void {
    if (action.data?.target_section) {
      // Dispatch custom event for specific section refresh
      const event = new CustomEvent('refreshSection', {
        detail: { section: action.data.target_section },
      });
      globalThis.dispatchEvent(event);
    } else {
      // General page refresh
      globalThis.location.reload();
    }
  }

  /**
   * Handle UI edit actions
   */
  private handleUIEdit(action: any): void {
    if (action.data?.editor_id && action.data?.content) {
      const event = new CustomEvent('updateEditor', {
        detail: {
          editorId: action.data.editor_id,
          content: action.data.content,
        },
      });
      globalThis.dispatchEvent(event);
    }
  }

  /**
   * Handle navigation actions
   */
  private handleNavigation(action: any): void {
    if (action.data?.navigation_path) {
      globalThis.history.pushState({}, '', action.data.navigation_path);
    }
  }

  /**
   * Handle diff display actions
   */
  private handleShowDiff(action: any): void {
    if (action.data?.diff_old && action.data?.diff_new) {
      const event = new CustomEvent('showDiff', {
        detail: {
          title: action.title,
          oldContent: action.data.diff_old,
          newContent: action.data.diff_new,
        },
      });
      globalThis.dispatchEvent(event);
    }
  }

  /**
   * Get current project ID from URL
   */
  private getCurrentProjectId(): number {
    const pathMatch = globalThis.location.pathname.match(/\/project\/(\d+)/);
    return pathMatch ? Number.parseInt(pathMatch[1], 10) : 0;
  }

  /**
   * Handle agent delegation actions
   */
  private handleAgentDelegation(action: any): void {
    // This is handled automatically by backend, just log for transparency
  }

  /**
   * Handle ADR suggestions actions
   */
  private handleADRSuggestions(action: any): void {
    
    // Dispatch event to refresh ADR components
    const event = new CustomEvent('adrSuggestionsGenerated', {
      detail: {
        suggestions: action.data?.content,
        title: action.title,
        description: action.description,
      },
    });
    globalThis.dispatchEvent(event);
  }

  /**
   * Handle ADR creation actions
   */
  private handleADRCreate(action: any): void {
    
    // If this action requires confirmation for generation
    if (action.confirmation_required && action.data?.target_agent === 'adr_generation') {
      const wantsGeneration = globalThis.confirm(`${action.title}\n\n${action.description}`);
      
      if (wantsGeneration) {
        // Trigger ADR content generation
        const projectId = this.getCurrentProjectId();
        this.confirmAction(projectId, action).catch(error => {
          logger.error('Failed to start ADR generation:', error);
        });
      } else {
        // Just refresh the ADR definitions view
        const event = new CustomEvent('adrDefinitionCreated', {
          detail: {
            definition: action.data?.content,
            title: action.title,
            description: action.description,
          },
        });
        globalThis.dispatchEvent(event);
      }
    } else {
      // Standard ADR creation notification
      const event = new CustomEvent('adrDefinitionCreated', {
        detail: {
          definition: action.data?.content,
          title: action.title,
          description: action.description,
        },
      });
      globalThis.dispatchEvent(event);
    }
  }

  /**
   * Handle ADR generation actions
   */
  private handleADRGenerate(action: any): void {
    
    if (action.confirmation_required) {
      const confirmed = globalThis.confirm(`${action.title}\n\n${action.description}\n\nWould you like to save this ADR?`);
      
      if (confirmed) {
        // Dispatch event to save ADR content
        const event = new CustomEvent('adrContentGenerated', {
          detail: {
            content: action.data?.content,
            adrId: action.data?.target_id,
            title: action.title,
            description: action.description,
          },
        });
        globalThis.dispatchEvent(event);
      }
    }
  }

  /**
   * Handle ADR summary actions
   */
  private handleADRSummary(action: any): void {
    
    if (action.confirmation_required) {
      const confirmed = globalThis.confirm(`${action.title}\n\n${action.description}\n\nWould you like to update the architecture section?`);
      
      if (confirmed) {
        // Dispatch event to update architecture section with ADR summary
        const event = new CustomEvent('updateArchitectureSection', {
          detail: {
            section: action.data?.target_section || 'adr_summary',
            content: action.data?.content,
            title: action.title,
            description: action.description,
          },
        });
        globalThis.dispatchEvent(event);
      }
    }
  }
}

// Export singleton instance
export const chatService = new ChatService();
export default chatService;
